import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXXL } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img/index.tsx';
import {
  NextSetStartCountdown,
  useNextSetStartTimer,
} from '@sorare/core/src/components/commonGame/NextSetStartCountdown/index.tsx';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation/index.tsx';
import { ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { SetBrandingImage } from '@sorare/core/src/set/branding/SetBrandingImage.tsx';
import { useCardSetBranding } from '@sorare/core/src/set/branding/useCardSetBranding.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { ClaimWelcomeBackRewards } from './ClaimWelcomeBackRewards/index.tsx';
import type {
  ClaimStarterPack_cardSet,
  ClaimStarterPack_cardSetRoot,
  ClaimStarterPack_currentUser,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  isolation: isolate;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  position: relative;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const Content = styled(Vertical).attrs({ center: true, gap: 4 })`
  position: relative;
  z-index: 1;
  flex: 1;
  padding: var(--triple-unit);
  justify-content: center;
  padding-bottom: calc(10 * var(--unit));

  @media ${laptopAndAbove} {
    margin: calc(6 * var(--unit)) 0;
    gap: 104px;
    justify-content: center;
  }
`;

const CTAWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  text-align: center;
`;

const PlayersLeft = styled.img`
  position: absolute;
  left: 0;
  bottom: 0;
  max-width: calc(40 * var(--vw));
`;

const PlayersRight = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  max-width: calc(40 * var(--vw));
`;

const PacksImage = styled(Img)`
  max-width: 100%;
  margin-bottom: var(--double-unit);
`;
const PlayersImage = styled(Img)`
  max-width: 100%;
  margin-bottom: -70px;
  margin-top: calc(-10 * var(--unit));
`;

const TextWrapper = styled.div`
  > * {
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.55));
  }

  &.multiline {
    width: min-content;
    word-spacing: var(--100vw);
  }
`;

type Props = {
  currentUser?: Nullable<ClaimStarterPack_currentUser>;
  cardSetRoot?: Nullable<ClaimStarterPack_cardSetRoot>;
  refetch: () => void;
};
export const ClaimStarterPack = ({
  currentUser,
  cardSetRoot,
  refetch,
}: Props) => {
  const { setBackgroundUrl } = useCardSetBranding();
  const { generateSportContextPath } = useSportContext();
  const { showTimer, starts, currentSet, nextSet } = useNextSetStartTimer();
  const isLaptopAndAbove = useIsLaptop();
  const seasonOver = !currentSet && !nextSet;

  // When the season is over (no current nor next set) fall back to the
  // previous set so the page keeps its branding instead of a static guess.
  const previousSet = cardSetRoot?.previousSet;

  const sportHasAnySet = !!(currentSet || nextSet || previousSet);

  const brandingSet = showTimer
    ? cardSetRoot?.nextSet
    : (cardSetRoot?.currentSet ?? previousSet);
  const branding = brandingSet?.branding;

  const backgroundUrl = isLaptopAndAbove
    ? branding?.countdownBackgroundDesktop
    : (branding?.countdownBackgroundMobile ??
      branding?.countdownBackgroundDesktop);

  if (!sportHasAnySet) {
    return null;
  }

  return (
    <>
      <Root>
        <Background
          style={
            backgroundUrl
              ? { background: `url(${backgroundUrl}) center / cover no-repeat` }
              : (currentSet || nextSet || previousSet) && setBackgroundUrl
                ? {
                    background: `url(${setBackgroundUrl}) center / cover no-repeat`,
                  }
                : undefined
          }
        />
        {!!(isLaptopAndAbove && branding?.countdownPlayersDesktopLeft) && (
          <PlayersLeft src={branding.countdownPlayersDesktopLeft} alt="" />
        )}
        {!!(isLaptopAndAbove && branding?.countdownPlayersDesktopRight) && (
          <PlayersRight src={branding.countdownPlayersDesktopRight} alt="" />
        )}
        <Content gap={1.5} center>
          <SetBrandingImage
            url={branding?.setLogo}
            height={48}
            color="var(--c-white)"
          />
          {starts ? (
            <Vertical center gap={2}>
              <TextWrapper className={classNames({ multiline: !seasonOver })}>
                <BrandXXL
                  className="text-gradient-color text-center"
                  style={
                    {
                      '--text-gradient-color': 'var(--c-gradient-common)',
                    } as CSSProperties
                  }
                >
                  {seasonOver ? (
                    <FormattedMessage defaultMessage="The season is over" />
                  ) : (
                    <FormattedMessage defaultMessage="Starts now" />
                  )}
                </BrandXXL>
              </TextWrapper>
              {!!seasonOver && (
                <BrandM className="text-center">
                  <FormattedMessage defaultMessage="Resting up for the comeback" />
                </BrandM>
              )}
            </Vertical>
          ) : (
            <NextSetStartCountdown onEnded={refetch} />
          )}
          <CTAWrapper>
            {!isLaptopAndAbove &&
              (starts && !seasonOver
                ? !!branding?.countdownPackMobile && (
                    <PacksImage
                      src={branding.countdownPackMobile}
                      alt=""
                      width="670"
                      height="464"
                    />
                  )
                : !!branding?.countdownPlayersMobile && (
                    <PlayersImage
                      src={branding.countdownPlayersMobile}
                      width="750"
                      height="884"
                      alt=""
                    />
                  ))}
            {!seasonOver && (
              <Button
                size="large"
                color="primary"
                blur={!currentSet}
                to={generateSportContextPath(
                  ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION
                )}
                fullWidth
                disabled={!currentSet}
              >
                <FormattedMessage defaultMessage="Claim my starter pack" />
              </Button>
            )}
          </CTAWrapper>
        </Content>
        <AbsoluteNav />
      </Root>
      {!!currentUser && (
        <ClaimWelcomeBackRewards
          currentUser={currentUser}
          cardSet={brandingSet}
        />
      )}
    </>
  );
};

const CARD_SET_FRAGMENT = gql`
  fragment ClaimStarterPack_cardSet on CardSet {
    slug
    branding {
      id
      setLogo: assetUrl(slot: BRANDING_SET_LOGO_COMPOSITE_MOBILE)
      countdownPlayersMobile: assetUrl(
        slot: ONBOARDING_SET_COUNTDOWN_PLAYERS_MOBILE
      )
      countdownPlayersDesktopLeft: assetUrl(
        slot: ONBOARDING_SET_COUNTDOWN_PLAYERS_DESKTOP_LEFT
      )
      countdownPlayersDesktopRight: assetUrl(
        slot: ONBOARDING_SET_COUNTDOWN_PLAYERS_DESKTOP_RIGHT
      )
      countdownPackMobile: assetUrl(slot: ONBOARDING_SET_COUNTDOWN_PACK_MOBILE)
      countdownBackgroundDesktop: assetUrl(
        slot: ONBOARDING_SET_COUNTDOWN_DESKTOP
      )
      countdownBackgroundMobile: assetUrl(slot: ONBOARDING_SET_COUNTDOWN_MOBILE)
    }
    ...ClaimWelcomeBackRewards_cardSet
  }
  ${ClaimWelcomeBackRewards.fragments.cardSet}
` as TypedDocumentNode<ClaimStarterPack_cardSet>;

ClaimStarterPack.fragments = {
  currentUser: gql`
    fragment ClaimStarterPack_currentUser on CurrentUser {
      slug
      ...ClaimWelcomeBackRewards_currentUser
    }
    ${ClaimWelcomeBackRewards.fragments.currentUser}
  ` as TypedDocumentNode<ClaimStarterPack_currentUser>,
  cardSetRoot: gql`
    fragment ClaimStarterPack_cardSetRoot on CardSetRoot {
      id
      currentSet(sport: $sport) {
        slug
        ...ClaimStarterPack_cardSet
      }
      nextSet(sport: $sport) {
        slug
        ...ClaimStarterPack_cardSet
      }
      previousSet(sport: $sport) {
        slug
        ...ClaimStarterPack_cardSet
      }
    }
    ${CARD_SET_FRAGMENT}
  ` as TypedDocumentNode<ClaimStarterPack_cardSetRoot>,
};
